/* tslint:disable:jsx-no-lambda WI:47606 */
import * as React from 'react';
import CalendarAllDayWell from './CalendarAllDayWell';
import CalendarDayConfig from '../interfaces/CalendarDayConfig';
import CalendarDayContent from '../interfaces/CalendarDayContent';
import CalendarGrid from './CalendarGrid';
import FocusableSurfaceComponent from './FocusableSurfaceComponent';
import SurfaceConstants from '../styles/SurfaceConstants';
import SurfaceStore from '../interfaces/SurfaceStore';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import toPixelString from '../utils/toPixelString';
import { attachBehaviors } from './BehaviorsHelper';
import { DEFAULT_HEADER_WIDTH } from '../utils/constants';
import { logMountingPerformance } from 'owa-analytics';
import { observer } from 'mobx-react';
import {
    addNewGridViewRef,
    addNewAllDayPanelViewRef,
    addNewInDayScrollRef,
    resetCalendarDayListBounds,
} from '../actions/calendarDayListBoundsMutators';

let styles = require('./CalendarDayList.scss');
let classNames = require('classnames/bind').bind(styles);

const MIN_BOUNDS_PADDING = 16;
/**
 * Props for calendar day list control
 */
export interface CalendarDayListProps {
    config: CalendarDayConfig;
    store: SurfaceStore;
}
/**
 * CalendarDayList shows calendar day grids with the ability to show calendar views side by side for each selected calendar.
 */
@logMountingPerformance('CalendarDayList')
@observer
export default class CalendarDayList extends React.Component<CalendarDayListProps, {}> {
    private gridHeadersScrollRef: HTMLElement;
    private allDayScrollRef: HTMLElement;

    render() {
        // clear the existing refs, so we can rebuild new list
        resetCalendarDayListBounds(this.props.store);

        let { config } = this.props;

        if (!config.calendarDayContentConfigProvider) {
            return null;
        }

        let calendarContentConfigs = config.calendarDayContentConfigProvider();
        if (!calendarContentConfigs || calendarContentConfigs.length <= 0) {
            return null;
        }

        let headerWidth = config.timeHeaderWidth || DEFAULT_HEADER_WIDTH;
        let headerPixelString = toPixelString(headerWidth);

        let itemsPanelStyle: React.CSSProperties = {
            height: toPixelString(
                SurfaceConstants.HourRowHeight *
                    (TimeConstants.MillisecondsInOneDay / TimeConstants.MillisecondsIn1Hour)
            ),
        };

        let minWidthStyle = config.minWidthProvider && {
            minWidth: config.minWidthProvider() + 'px',
        };

        // track the weather icon to be shown only once and not for all calendars
        let showedHeaderAddOnOnce: boolean = false;

        return (
            <FocusableSurfaceComponent
                tabOrderProvider={() =>
                    this.mergeTabs(
                        calendarContentConfigs.map(config => {
                            return config.content;
                        })
                    )
                }
                setFocusedElement={config.setFocusedElement}
                ref={config.surfaceComponent}>
                <div className={styles.page}>
                    <div className={styles.topPanel}>
                        {attachBehaviors(
                            <div
                                style={{ width: headerPixelString, display: 'flex', flexShrink: 0 }}
                            />,
                            calendarContentConfigs[0].timeStripHeaderBehaviors
                        )}
                        <div
                            className={styles.scrollableContainer}
                            onScroll={this.allDayPanelScroll}
                            ref={this.allDayScrollRefFunction}>
                            {calendarContentConfigs.map((contentConfig, i) => {
                                let hideHeaderAddOn = showedHeaderAddOnOnce;
                                showedHeaderAddOnOnce = true;
                                let providerContent = contentConfig.content;
                                return (
                                    <div
                                        key={i}
                                        className={styles.topContainer}
                                        style={minWidthStyle}
                                        ref={gridElement =>
                                            this.allDayPanelViewRefsFunction(gridElement)
                                        }>
                                        {!config.hideTopHeaders &&
                                            calendarContentConfigs.length > 1 &&
                                            contentConfig.contentDescription && (
                                                <div className={styles.contentHeader}>
                                                    {contentConfig.contentDescription()}
                                                </div>
                                            )}
                                        {!config.hideTopHeaders &&
                                            attachBehaviors(
                                                <div
                                                    className={classNames(styles.header, {
                                                        stackedHeader:
                                                            calendarContentConfigs.length > 1,
                                                    })}>
                                                    {providerContent.headers(hideHeaderAddOn)}
                                                </div>,
                                                contentConfig.allDayBehaviors
                                            )}
                                        <CalendarAllDayWell
                                            className={styles.allDayPanel}
                                            config={contentConfig}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {/* In-Day panel */}
                    <div className={classNames(styles.inDayPanel)}>
                        <div
                            className={styles.gridHeadersScrollContainer}
                            ref={this.gridHeadersScrollRefFunction}>
                            {attachBehaviors(
                                <div className={styles.timeContainer} />,
                                calendarContentConfigs[0].timeStripBehaviors
                            )}
                        </div>
                        {attachBehaviors(
                            <div
                                className={classNames(styles.scrollContainer, 'customScrollBar')}
                                onScroll={this.inDayPanelScroll}
                                data-is-scrollable={true}
                                ref={this.inDayScrollRefFunction}>
                                <div className={styles.growFlexBox} style={itemsPanelStyle}>
                                    {calendarContentConfigs.map((contentAndBehaviorConfig, i) => (
                                        <div
                                            key={i}
                                            className={styles.renderPanel}
                                            style={minWidthStyle}
                                            ref={
                                                gridElement =>
                                                    this.gridViewRefsFunction(gridElement) // passing an arrow function here ensures that a new function instance is created everytime, and as a result the ref function gets called even when same element updates( which is case when component udates due to change in calendar selection)
                                            }>
                                            <CalendarGrid
                                                contentConfig={contentAndBehaviorConfig}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>,
                            calendarContentConfigs[0].inDayBehaviors || []
                        )}
                    </div>
                </div>
            </FocusableSurfaceComponent>
        );
    }

    private inDayPanelScroll = e => {
        const container: HTMLDivElement = e.currentTarget;
        this.gridHeadersScrollRef && (this.gridHeadersScrollRef.scrollTop = container.scrollTop);
        this.allDayScrollRef && (this.allDayScrollRef.scrollLeft = container.scrollLeft);
    };

    private allDayPanelScroll = e => {
        const container: HTMLDivElement = e.currentTarget;
        // move the inDayPanel along with the allDayPanel to keep them aligned
        this.props.store.calendarDayListBounds.inDayScrollRef &&
            (this.props.store.calendarDayListBounds.inDayScrollRef.element.scrollLeft =
                container.scrollLeft);
    };

    private mergeTabs(contents: CalendarDayContent[]): string[] {
        let results = [];
        // When merging tabs we have to remove surface headers from all except first content stream
        // and add the contentHeaders
        contents.forEach((content, index) => {
            results.push('contentHeader_' + index);
            results.push(
                ...content
                    .tabOrderProvider()
                    .filter(tabId => index == 0 || tabId.search('surfaceHeader_') < 0)
            );
        });
        return results;
    }

    public getPopupBounds = (anchorElement: HTMLElement) => {
        if (
            this.props.store.calendarDayListBounds.gridViewRefs &&
            this.props.store.calendarDayListBounds.gridViewRefs.length > 0 &&
            this.props.store.calendarDayListBounds.inDayScrollRef
        ) {
            // Check to see if the anchorElement is part of any gridViewRefs
            let currentRef = this.props.store.calendarDayListBounds.gridViewRefs.filter(
                a => a && a.element.contains(anchorElement)
            );
            if (currentRef.length) {
                const scrollBounds = this.props.store.calendarDayListBounds.inDayScrollRef.bounds;
                const scrollLeft = this.props.store.calendarDayListBounds.inDayScrollRef.element
                    .scrollLeft;
                return {
                    top: scrollBounds.top + MIN_BOUNDS_PADDING,
                    bottom: scrollBounds.bottom - MIN_BOUNDS_PADDING,
                    height: scrollBounds.height - MIN_BOUNDS_PADDING * 2,
                    left: scrollBounds.left - scrollLeft + MIN_BOUNDS_PADDING,
                    right: scrollBounds.right - scrollLeft - MIN_BOUNDS_PADDING,
                    width: scrollBounds.width - MIN_BOUNDS_PADDING * 2,
                };
            } else {
                // if we did not find anchorElement to be part of any gridViewRefs, then check if it is part of allDayPanelViewRefs
                if (
                    this.props.store.calendarDayListBounds.allDayPanelViewRefs &&
                    this.props.store.calendarDayListBounds.allDayPanelViewRefs.length > 0
                ) {
                    currentRef = this.props.store.calendarDayListBounds.allDayPanelViewRefs.filter(
                        a => a && a.element.contains(anchorElement)
                    );
                    if (currentRef.length) {
                        const bounds = currentRef[currentRef.length - 1].bounds;
                        const scrollBounds = this.props.store.calendarDayListBounds.inDayScrollRef
                            .bounds;
                        const scrollLeft = this.props.store.calendarDayListBounds.inDayScrollRef
                            .element.scrollLeft;
                        if (bounds) {
                            return {
                                top: bounds.top,
                                bottom: Number.POSITIVE_INFINITY, // no bounds for bottom
                                height: Number.POSITIVE_INFINITY, // no bounds for height
                                left: scrollBounds.left - scrollLeft + MIN_BOUNDS_PADDING,
                                right: scrollBounds.right - scrollLeft - MIN_BOUNDS_PADDING,
                                width: scrollBounds.width - MIN_BOUNDS_PADDING * 2,
                            };
                        }
                    }
                }
            }
        }

        // if anchor is not in the component, we have no constraints
        return null;
    };

    private gridViewRefsFunction = (element: HTMLElement) => {
        if (element) {
            setTimeout(() => {
                // build a list of grid refs per calendar, so that we can anchor peeks
                // based on which calendar grid the clicked item belongs to.
                addNewGridViewRef(element, this.props.store);
            }, 0);
        }
    };

    private allDayPanelViewRefsFunction = (element: HTMLElement) => {
        if (element) {
            setTimeout(() => {
                // build a list of grid refs per calendar, so that we can anchor peeks
                // based on which calendar grid the clicked item belongs to.
                addNewAllDayPanelViewRef(element, this.props.store);
            }, 0);
        }
    };

    public inDayScrollRefFunction = (element: HTMLElement) => {
        if (element) {
            setTimeout(() => {
                addNewInDayScrollRef(element, this.props.store);
            }, 0);
        }
    };

    private gridHeadersScrollRefFunction = (element: HTMLElement) => {
        this.gridHeadersScrollRef = element;
    };

    private allDayScrollRefFunction = (element: HTMLElement) => {
        this.allDayScrollRef = element;
    };
}
